import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "../components/Toast/useToast";
import { db } from "../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

export default function FormularioPRL() {
  const { id } = useParams();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [solicitud, setSolicitud] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const [formData, setFormData] = useState({
    encargadoObra: "",
    numTrabajadores: "",
    horario: "",
    tareasDesarrolladas: "",
    elecNoIntervencion: false,
    elecIntervBasicas: false,
    elecOperMantenimiento: false,
    trabAutorizados: false,
    trabCualificados: false,
    equipos: Array(8).fill(false),
    otrosEquipos: "",
    equiposDetalle: "",
    condiciones: {
      lug11: { si: false, no: false, np: false },
      lug12: { si: false, no: false, np: false },
      lug13: { si: false, no: false, np: false },
      lug14: { si: false, no: false, np: false },
      lug15: { si: false, no: false, np: false },
      en21:  { si: false, no: false, np: false },
      en22:  { si: false, no: false, np: false },
      eq31:  { si: false, no: false, np: false },
      eq32:  { si: false, no: false, np: false }
    },
    emergencias: "",
    aceptaPoliticas: false,
    tecnicoFinal: "",
    firmaFinal: null
  });

  useEffect(() => { loadSolicitud(); }, [id]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      canvas.width = w * 2;
      canvas.height = h * 2;
      const ctx = canvas.getContext("2d");
      ctx.scale(2, 2);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#1a3a6b";
    }
  }, []);

  const loadSolicitud = async () => {
    try {
      setLoading(true);
      const docSnap = await getDoc(doc(db, "work_requests", id));
      if (!docSnap.exists()) { showToast({ type: "error", message: "Solicitud no encontrada" }); return; }
      const data = docSnap.data();
      if (!data.solicitarPRL) { showToast({ type: "error", message: "Esta solicitud no requiere PRL" }); return; }
      setSolicitud({ id: docSnap.id, ...data });
    } catch (e) {
      showToast({ type: "error", message: `Error: ${e.message}` });
    } finally {
      setLoading(false);
    }
  };

  const getPointer = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX || e.touches?.[0]?.clientX;
    const cy = e.clientY || e.touches?.[0]?.clientY;
    return { x: (cx - rect.left) / (rect.width / canvas.width), y: (cy - rect.top) / (rect.height / canvas.height) };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current; if (!canvas) return;
    setIsDrawing(true);
    const ctx = canvas.getContext("2d");
    const p = getPointer(e, canvas);
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const p = getPointer(e, canvas);
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };

  const stopDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(false);
    if (canvasRef.current) setFormData(prev => ({ ...prev, firmaFinal: canvasRef.current.toDataURL() }));
  };

  const clearSignature = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setFormData(prev => ({ ...prev, firmaFinal: null }));
  };

  const toggleCheck    = (f) => setFormData(prev => ({ ...prev, [f]: !prev[f] }));
  const toggleEquipo   = (i) => setFormData(prev => ({ ...prev, equipos: prev.equipos.map((v, j) => j === i ? !v : v) }));
  const toggleCondicion = (key, tipo) => setFormData(prev => ({
    ...prev, condiciones: { ...prev.condiciones, [key]: { si: tipo === "si", no: tipo === "no", np: tipo === "np" } }
  }));
  const handleChange = (f, v) => setFormData(prev => ({ ...prev, [f]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.encargadoObra.trim()) { showToast({ type: "error", message: "El encargado de obra es obligatorio" }); return; }
    if (!formData.aceptaPoliticas)       { showToast({ type: "error", message: "Debes aceptar las politicas" }); return; }
    if (!formData.firmaFinal)            { showToast({ type: "error", message: "La firma es obligatoria" }); return; }

    try {
      setSubmitting(true);
      await updateDoc(doc(db, "work_requests", id), {
        prlCompletado: true, prlDatos: formData,
        prlCompletadoAt: serverTimestamp(), updatedAt: serverTimestamp()
      });

      // Generar PDF con pdfmake y enviar por email
      try {
        const GENERATE_PRL_URL = "https://us-central1-firma-digital-db89b.cloudfunctions.net/generatePRLPdf";
        if (solicitud) {
          // Cargar pdfmake via script tags (CDN) para que 'this' sea window
          // El import() de Vite ejecuta en modo ESM donde this=undefined, rompiendo vfs_fonts
          const loadScript = (src) => new Promise((resolve, reject) => {
            const existing = document.querySelector(`script[src="${src}"]`);
            if (existing) { resolve(); return; }
            const s = document.createElement("script");
            s.src = src;
            s.onload = resolve;
            s.onerror = () => reject(new Error("No se pudo cargar: " + src));
            document.head.appendChild(s);
          });

          if (!window.pdfMake) {
            await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/pdfmake.min.js");
          }
          if (!window.pdfMake?.vfs) {
            await loadScript("https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.2.10/vfs_fonts.min.js");
          }
          const pdfMake = window.pdfMake;
          if (!pdfMake || typeof pdfMake.createPdf !== "function") {
            throw new Error("pdfMake no disponible tras carga CDN");
          }

          console.log("[PRL] pdfMake OK, vfs keys:", Object.keys(pdfMake.vfs||{}).length);

          const NAVY = "#0f2a4a"; const BLUE = "#1a3a6b"; const LBLUE = "#dbeafe";
          const RED  = "#8b0000"; const GRAY = "#555555";
          const chk = (v) => v ? "[X]" : "[ ]";

          const equiposLabels = [
            "1. Uso de equipos de riesgo electrico",
            "2. Utilizacion de herramientas electricas",
            "3. Escalera/Plataforma elevadora",
            "4. Equipos manutencion de cargas",
            "5. Utiles manejo mecanico",
            "6. Herramientas manuales",
            "7. Balizar area de trabajo",
            "8. Trabajos en sotanos"
          ];

          const condDefs = [
            { id:"lug11", label:"1.1 Espacios con riesgo incendio/explosion",    sub:"1. Lugares de trabajo" },
            { id:"lug12", label:"1.2 Riesgo caidas a distinto nivel" },
            { id:"lug13", label:"1.3 Riesgo caidas al mismo nivel" },
            { id:"lug14", label:"1.4 Equipos moviles con riesgo atropellos" },
            { id:"lug15", label:"1.5 Existencia de cargas suspendidas" },
            { id:"en21",  label:"2.1 Instalaciones con riesgo electrico",         sub:"2. Energias" },
            { id:"en22",  label:"2.2 Otras energias (Hidraulica, neumatica...)" },
            { id:"eq31",  label:"3.1 Equipos con riesgos especiales",              sub:"3. Equipos de trabajo" },
            { id:"eq32",  label:"3.2 Equipos elevacion de personas" },
          ];

          const condRows = [];
          condDefs.forEach(c => {
            if (c.sub) condRows.push([{ text: c.sub, bold: true, fontSize: 8, colSpan: 4, fillColor: "#e8e8e8" }, {}, {}, {}]);
            const v = formData.condiciones[c.id] || {};
            condRows.push([
              { text: c.label, fontSize: 8 },
              { text: chk(v.si), alignment: "center", fontSize: 9 },
              { text: chk(v.no), alignment: "center", fontSize: 9 },
              { text: chk(v.np), alignment: "center", fontSize: 9 },
            ]);
          });

          const equiposGrid = [];
          for (let row = 0; row < 4; row++) {
            equiposGrid.push([
              { text: chk(formData.equipos[row * 2]),     alignment: "center", fontSize: 9 },
              { text: equiposLabels[row * 2] || "",        fontSize: 8 },
              { text: chk(formData.equipos[row * 2 + 1]), alignment: "center", fontSize: 9 },
              { text: equiposLabels[row * 2 + 1] || "",   fontSize: 8 },
            ]);
          }

          const firmaStack = [];
          if (formData.firmaFinal) {
            try { firmaStack.push({ image: formData.firmaFinal, width: 140, height: 55 }); } catch(e) {}
          }
          firmaStack.push({ text: formData.tecnicoFinal || "-", fontSize: 9, margin: [0, 4, 0, 0] });

          const docDef = {
            pageSize: "A4", pageMargins: [30, 30, 30, 30],
            defaultStyle: { font: "Roboto", fontSize: 9 },
            content: [
              { table: { widths: ["*"], body: [[{ stack: [
                { text: "PLAN DE PREVENCION DE RIESGOS LABORALES", bold: true, fontSize: 13, color: "white" },
                { text: "Documento de coordinacion de actividades empresariales", fontSize: 8, color: "#C5D8F5", margin: [0,2,0,0] }
              ], fillColor: BLUE, margin: [10,10,10,10] }]]}, layout: "noBorders", margin: [0,0,0,10] },
              { table: { widths: ["*"], body: [[{ stack: [
                { text: "Obra: " + (solicitud.codigoObra || "-"), bold: true, color: BLUE, fontSize: 10 },
                { text: "Direccion: " + (solicitud.direccionObra || "-"), fontSize: 9, margin: [0,2,0,0] },
                { text: "Fechas: " + (solicitud.fechasActuacion || solicitud.rangoFechas || new Date().toLocaleDateString("es-ES")), fontSize: 9, color: BLUE, margin: [0,2,0,0] }
              ], margin: [8,8,8,8], fillColor: LBLUE }]]}, layout: { hLineColor: () => "#3b82f6", vLineColor: () => "#3b82f6" }, margin: [0,0,0,10] },
              { text: "Datos Generales", bold: true, fontSize: 10, color: BLUE, margin: [0,0,0,4] },
              { table: { widths: [140,"*"], body: [
                [{ text: "Encargado de Obra *", color: GRAY }, { text: formData.encargadoObra || "-" }],
                [{ text: "N de Trabajadores",   color: GRAY }, { text: formData.numTrabajadores || "-" }],
                [{ text: "Horario",              color: GRAY }, { text: formData.horario || "-" }],
                [{ text: "Tareas desarrolladas", color: GRAY }, { text: formData.tareasDesarrolladas || "-" }],
                [{ text: "Responsable PRL",      color: GRAY }, { text: solicitud.responsablePRL?.nombre || "-" }],
              ]}, layout: "lightHorizontalLines", margin: [0,0,0,10] },
              { text: "Electricidad", bold: true, fontSize: 10, color: BLUE, margin: [0,4,0,4] },
              { columns: [
                { width: "50%", stack: [
                  { text: chk(formData.elecNoIntervencion)    + "  No hay intervencion electrica", fontSize: 9, margin:[0,0,0,3] },
                  { text: chk(formData.elecIntervBasicas)     + "  Intervenciones basicas",        fontSize: 9, margin:[0,0,0,3] },
                  { text: chk(formData.elecOperMantenimiento) + "  Oper. mantenimiento",            fontSize: 9 },
                ]},
                { width: "50%", stack: [
                  { text: chk(formData.trabAutorizados)  + "  Trabajadores autorizados",  fontSize: 9, margin:[0,0,0,3] },
                  { text: chk(formData.trabCualificados) + "  Trabajadores cualificados", fontSize: 9 },
                ]},
              ], margin: [0,0,0,10] },
              { text: "Relacion de equipos a utilizar", bold: true, fontSize: 10, color: BLUE, margin: [0,4,0,4] },
              { table: { widths: ["8%","42%","8%","42%"], body: equiposGrid }, layout: "lightHorizontalLines", margin: [0,0,0,10] },
              { text: "Condiciones de riesgo de las instalaciones", bold: true, fontSize: 10, color: BLUE, margin: [0,4,0,4] },
              { table: { widths: ["*", 35, 35, 50], headerRows: 1, body: [
                [
                  { text: "Condiciones de riesgo", bold: true, fontSize: 8, fillColor: NAVY, color: "white" },
                  { text: "SI",         bold: true, fontSize: 8, fillColor: RED,  color: "white", alignment: "center" },
                  { text: "NO",         bold: true, fontSize: 8, fillColor: RED,  color: "white", alignment: "center" },
                  { text: "NO PROCEDE", bold: true, fontSize: 8, fillColor: RED,  color: "white", alignment: "center" },
                ],
                ...condRows
              ]}, layout: "lightHorizontalLines", margin: [0,0,0,10] },
              { text: "Emergencias:", bold: true, fontSize: 9, color: GRAY },
              { text: formData.emergencias || "-", fontSize: 9, margin: [0,2,0,10] },
              { text: "Firma y Conformidad", bold: true, fontSize: 10, color: BLUE, margin: [0,4,0,6] },
              { columns: [
                { width: "55%", stack: [
                  { text: "Tecnico / Encargado responsable", fontSize: 8, color: GRAY },
                  { text: formData.tecnicoFinal || "-", fontSize: 10, bold: true, margin: [0,4,0,0] },
                ]},
                { width: "45%", stack: firmaStack },
              ]},
              { text: "Completado el " + new Date().toLocaleString("es-ES"), fontSize: 8, color: GRAY, margin: [0,10,0,0] },
            ]
          };

          // Generar el PDF en el browser con la API correcta
          console.log("[PRL] Llamando createPdf...");
          const pdfBase64 = await new Promise((resolve, reject) => {
            try {
              pdfMake.createPdf(docDef).getBase64((b64) => {
                console.log("[PRL] getBase64 resultado, longitud:", b64?.length);
                if (b64) resolve(b64);
                else reject(new Error("pdfmake getBase64 returned empty"));
              });
            } catch(err) {
              console.error("[PRL] Error en createPdf:", err);
              reject(err);
            }
          });
          console.log("[PRL] PDF generado, base64 length:", pdfBase64.length);

          const genRes = await fetch(GENERATE_PRL_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              requestId: id, pdfBase64,
              solicitudData: {
                codigoObra:      solicitud.codigoObra || id,
                direccionObra:   solicitud.direccionObra || "",
                fechasActuacion: solicitud.fechasActuacion || solicitud.rangoFechas || "",
                responsablePRL:  solicitud.responsablePRL || null,
                companyId:       solicitud.companyId || "cejm",
                requesterEmail:  solicitud.requesterEmail || null,
              }
            }),
          });
          const genData = await genRes.json().catch(() => ({}));
          if (!genRes.ok) console.error("Error generatePRLPdf:", genRes.status, genData);
          else console.log("PDF PRL enviado OK:", genData);
        }
      } catch (pdfError) {
        console.error("Error generando PDF PRL:", pdfError);
        // Mostrar error en pantalla para diagnóstico
        alert("ERROR PDF:\n" + (pdfError?.message || String(pdfError)));
      }

      showToast({ type: "success", message: "Formulario PRL enviado correctamente" });
      setTimeout(() => window.location.href = "/", 8000); // 8 segundos para ver logs
    } catch (error) {
      console.error("Error:", error);
      alert("ERROR PRINCIPAL:\n" + (error?.message || String(error)) + "\n\nStack: " + error?.stack?.substring(0,300));
      showToast({ type: "error", message: `Error: ${error.message}` });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ padding: "60px 16px", textAlign: "center", minHeight: "100vh", background: "linear-gradient(145deg, #1a3a6b 0%, #0d5c9e 50%, #1a7fd4 100%)" }}>
      <p style={{ color: "#fff", fontSize: "18px" }}>Cargando...</p>
    </div>
  );

  if (!solicitud) return (
    <div style={{ padding: "60px 16px", minHeight: "100vh", background: "linear-gradient(145deg, #1a3a6b 0%, #0d5c9e 50%, #1a7fd4 100%)" }}>
      <div style={{ maxWidth: "600px", margin: "0 auto", background: "#fff", borderRadius: "20px", padding: "40px", textAlign: "center" }}>
        <h2 style={{ color: "#ef4444" }}>Solicitud no encontrada</h2>
      </div>
    </div>
  );

  const equiposLabels = [
    "1. Uso de equipos de riesgo electrico",
    "2. Utilizacion de herramientas electricas",
    "3. Escalera/Plataforma elevadora",
    "4. Equipos manutencion de cargas",
    "5. Utiles manejo mecanico de cargas",
    "6. Herramientas manuales",
    "7. Balizar area de trabajo",
    "8. Trabajos en sotanos"
  ];

  const condicionesData = [
    { id: "lug11", label: "1.1.- Espacios clasificados con riesgo de incendio y explosion", subhdr: "1. Lugares de trabajo" },
    { id: "lug12", label: "1.2.- Instalaciones con riesgo de caidas a distinto nivel" },
    { id: "lug13", label: "1.3.- Instalaciones con riesgo de caidas al mismo nivel" },
    { id: "lug14", label: "1.4.- Existencia de equipos moviles con riesgo de atropellos" },
    { id: "lug15", label: "1.5.- Existencia de cargas suspendidas" },
    { id: "en21",  label: "2.1. Existencia de Instalaciones con riesgo electrico", subhdr: "2. Energias" },
    { id: "en22",  label: "2.2. Otras energias (Hidraulica, neumatica, termica...)" },
    { id: "eq31",  label: "3.1. Equipos con riesgos especiales", subhdr: "3. Equipos de trabajo" },
    { id: "eq32",  label: "3.2. Equipos para manejo mecanico y/o elevacion de personas" }
  ];

  return (
    <>
      <style>{`
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        :root { --blue: #0071e3; --text: #1d1d1f; --muted: #6e6e73; --border: #d1d1d6; --bg-field: #e8f0fe; }
        body { font-family: 'Inter', -apple-system, sans-serif; background: linear-gradient(145deg, #1a3a6b 0%, #0d5c9e 50%, #1a7fd4 100%); min-height: 100vh; padding: 24px 16px 60px; }
        .documento { max-width: 900px; margin: 0 auto; background: white; border-radius: 20px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); overflow: hidden; }
        .doc-header { background: #1a3a6b; padding: 20px 32px; }
        .doc-header-title { font-size: 18px; font-weight: 800; color: white; line-height: 1.3; }
        .doc-header-sub { font-size: 11px; color: rgba(255,255,255,0.7); font-weight: 500; margin-top: 4px; }
        .doc-body { padding: 24px 32px; }
        .campo-fila { display: flex; align-items: center; border-bottom: 1px solid var(--border); padding: 8px 0; gap: 12px; }
        .campo-label { width: 200px; flex-shrink: 0; font-size: 13px; color: var(--text); font-weight: 600; }
        .campo-input { flex: 1; background: var(--bg-field); border: 1px solid #b0c4de; border-radius: 6px; padding: 6px 10px; font-size: 13px; outline: none; color: var(--text); transition: border-color .15s; }
        .campo-input:focus { border-color: var(--blue); background: #dce8fd; }
        .seccion-sep { border-top: 2px solid #1a3a6b; margin: 20px 0 14px; }
        .seccion-titulo { font-size: 12px; font-weight: 800; color: var(--text); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px; }
        .check-row { display: flex; align-items: center; gap: 10px; margin-bottom: 7px; padding: 3px 0; }
        .check-box { width: 20px; height: 20px; border: 2px solid #444; border-radius: 3px; flex-shrink: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .15s; background: white; }
        .check-box.checked { background: #1a3a6b; border-color: #1a3a6b; }
        .check-box.checked::after { content: '\\2713'; color: white; font-size: 13px; font-weight: 900; }
        .check-label { font-size: 12px; font-weight: 600; color: var(--text); text-transform: uppercase; letter-spacing: 0.03em; }
        .grid-equipos { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border: 1px solid #ccc; border-radius: 4px; overflow: hidden; margin-bottom: 10px; }
        .equipo-cell { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-bottom: 1px solid #e0e0e0; border-right: 1px solid #e0e0e0; font-size: 11px; font-weight: 600; color: var(--text); text-transform: uppercase; }
        .equipo-cell:nth-child(2n) { border-right: none; }
        .sub-label { font-size: 11px; color: var(--muted); font-weight: 700; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.04em; }
        .firma-canvas-wrap { position: relative; background: var(--bg-field); border: 1px solid #b0c4de; border-radius: 6px; height: 70px; overflow: hidden; cursor: crosshair; }
        .firma-canvas-wrap canvas { display: block; width: 100%; height: 100%; touch-action: none; }
        .firma-clear-btn { position: absolute; top: 3px; right: 5px; background: rgba(255,69,58,0.15); color: #ff3b30; border: none; border-radius: 5px; padding: 2px 7px; font-size: 10px; font-weight: 700; cursor: pointer; }
        .btn-pdf { display: flex; align-items: center; justify-content: center; width: 100%; padding: 14px; background: #1a3a6b; color: white; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; margin-top: 28px; transition: background .15s; box-shadow: 0 4px 16px rgba(26,58,107,0.4); }
        .btn-pdf:hover { background: #0d2a50; }
        .btn-pdf:disabled { opacity: 0.6; cursor: not-allowed; }
        .tabla-cond { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 10px; }
        .tabla-cond th { background: #1a3a6b; color: white; padding: 6px 8px; font-size: 10px; text-align: center; font-weight: 700; }
        .tabla-cond th.desc { text-align: left; }
        .tabla-cond td { border: 1px solid #ccc; padding: 5px 8px; vertical-align: middle; }
        .tabla-cond tr.subhdr td { background: #e8edf5; font-weight: 800; text-transform: uppercase; font-size: 10px; }
        .td-check { text-align: center; width: 45px; }
        @media (max-width: 600px) { .doc-body { padding: 16px; } .campo-label { width: 120px; } .grid-equipos { grid-template-columns: 1fr; } }
      `}</style>

      <div className="documento">
        <div className="doc-header">
          <div className="doc-header-title">PLAN DE PREVENCION DE RIESGOS LABORALES</div>
          <div className="doc-header-sub">Documento de coordinacion de actividades empresariales</div>
        </div>

        <div className="doc-body">
          <form onSubmit={handleSubmit}>

            {/* INFO OBRA */}
            <div style={{ background: "#f0f9ff", padding: "16px", borderRadius: "8px", marginBottom: "20px", border: "1px solid #3b82f6" }}>
              <h3 style={{ color: "#1e40af", margin: "0 0 8px 0", fontSize: "15px" }}>Obra: {solicitud.codigoObra}</h3>
              <p style={{ margin: "0", color: "#1e3a8a", fontSize: "13px" }}><strong>Direccion:</strong> {solicitud.direccionObra}</p>
              <p style={{ margin: "4px 0 0 0", color: "#1e3a8a", fontSize: "13px" }}><strong>Fechas:</strong> {solicitud.fechasActuacion || solicitud.rangoFechas}</p>
            </div>

            {/* DATOS GENERALES */}
            <div className="campo-fila">
              <div className="campo-label">Encargado Obra *</div>
              <input className="campo-input" type="text" value={formData.encargadoObra} onChange={(e) => handleChange("encargadoObra", e.target.value)} required />
            </div>
            <div className="campo-fila">
              <div className="campo-label">N de Trabajadores</div>
              <input className="campo-input" type="text" value={formData.numTrabajadores} onChange={(e) => handleChange("numTrabajadores", e.target.value)} />
            </div>
            <div className="campo-fila">
              <div className="campo-label">Horario</div>
              <input className="campo-input" type="text" value={formData.horario} onChange={(e) => handleChange("horario", e.target.value)} />
            </div>
            <div className="campo-fila">
              <div className="campo-label">Tareas desarrolladas</div>
              <input className="campo-input" type="text" value={formData.tareasDesarrolladas} onChange={(e) => handleChange("tareasDesarrolladas", e.target.value)} />
            </div>

            <div className="seccion-sep"></div>

            {/* ELECTRICIDAD */}
            <div className="seccion-titulo">Electricidad</div>
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "200px", border: "1px solid #d0d7e2", borderRadius: "6px", padding: "10px 14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", marginBottom: "8px", color: "#1a3a6b" }}>Electricidad</div>
                <div className="check-row">
                  <div className={`check-box ${formData.elecNoIntervencion ? "checked" : ""}`} onClick={() => toggleCheck("elecNoIntervencion")}></div>
                  <span className="check-label">No hay intervencion</span>
                </div>
                <div className="check-row">
                  <div className={`check-box ${formData.elecIntervBasicas ? "checked" : ""}`} onClick={() => toggleCheck("elecIntervBasicas")}></div>
                  <span className="check-label">Intervenciones basicas</span>
                </div>
                <div className="check-row">
                  <div className={`check-box ${formData.elecOperMantenimiento ? "checked" : ""}`} onClick={() => toggleCheck("elecOperMantenimiento")}></div>
                  <span className="check-label">Oper. mantenimiento</span>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: "200px", border: "1px solid #d0d7e2", borderRadius: "6px", padding: "10px 14px" }}>
                <div style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", marginBottom: "8px", color: "#1a3a6b" }}>Nivel de riesgo electrico</div>
                <div className="check-row">
                  <div className={`check-box ${formData.trabAutorizados ? "checked" : ""}`} onClick={() => toggleCheck("trabAutorizados")}></div>
                  <span className="check-label">Trabajadores autorizados</span>
                </div>
                <div className="check-row">
                  <div className={`check-box ${formData.trabCualificados ? "checked" : ""}`} onClick={() => toggleCheck("trabCualificados")}></div>
                  <span className="check-label">Trabajadores cualificados</span>
                </div>
              </div>
            </div>

            <div className="seccion-sep"></div>

            {/* EQUIPOS */}
            <div className="seccion-titulo">Relacion de equipos que se van a utilizar</div>
            <div className="grid-equipos">
              {equiposLabels.map((label, index) => (
                <div key={index} className="equipo-cell">
                  <div className={`check-box ${formData.equipos[index] ? "checked" : ""}`} onClick={() => toggleEquipo(index)} style={{ flexShrink: 0 }}></div>
                  {label}
                </div>
              ))}
            </div>
            <div style={{ border: "1px solid #ccc", borderRadius: "4px", padding: "8px 10px", marginTop: "4px" }}>
              <div className="sub-label">Otros (especificar):</div>
              <input className="campo-input" type="text" style={{ width: "100%" }} value={formData.otrosEquipos} onChange={(e) => handleChange("otrosEquipos", e.target.value)} />
            </div>
            <div style={{ marginTop: "8px" }}>
              <div className="sub-label">Equipos utilizados</div>
              <input className="campo-input" type="text" style={{ width: "100%" }} value={formData.equiposDetalle} onChange={(e) => handleChange("equiposDetalle", e.target.value)} />
            </div>

            <div className="seccion-sep"></div>

            {/* CONDICIONES DE RIESGO */}
            <div className="seccion-titulo">Condiciones de riesgo de las instalaciones</div>
            <table className="tabla-cond">
              <thead>
                <tr>
                  <th className="desc">Condiciones de riesgo</th>
                  <th style={{ background: "#8b0000" }}>Medidas<br />a aplicar</th>
                  <th style={{ background: "#8b0000" }}>SI</th>
                  <th style={{ background: "#8b0000" }}>NO</th>
                  <th style={{ background: "#8b0000" }}>NO<br />PROCEDE</th>
                </tr>
              </thead>
              <tbody>
                {condicionesData.map((c) => (
                  <>
                    {c.subhdr && (
                      <tr key={`sub-${c.id}`} className="subhdr">
                        <td colSpan={5}>{c.subhdr}</td>
                      </tr>
                    )}
                    <tr key={c.id}>
                      <td className="desc">{c.label}</td>
                      <td className="td-check" style={{ background: "#fff9c4" }}>{"\u26A0"}</td>
                      <td className="td-check">
                        <div className={`check-box ${formData.condiciones[c.id].si ? "checked" : ""}`} onClick={() => toggleCondicion(c.id, "si")} style={{ margin: "auto" }}></div>
                      </td>
                      <td className="td-check">
                        <div className={`check-box ${formData.condiciones[c.id].no ? "checked" : ""}`} onClick={() => toggleCondicion(c.id, "no")} style={{ margin: "auto" }}></div>
                      </td>
                      <td className="td-check">
                        <div className={`check-box ${formData.condiciones[c.id].np ? "checked" : ""}`} onClick={() => toggleCondicion(c.id, "np")} style={{ margin: "auto" }}></div>
                      </td>
                    </tr>
                  </>
                ))}
              </tbody>
            </table>

            <div className="seccion-sep"></div>

            {/* EMERGENCIAS */}
            <div className="seccion-titulo">7. Emergencias:</div>
            <textarea className="campo-input" rows={3} style={{ width: "100%", resize: "vertical" }} value={formData.emergencias} onChange={(e) => handleChange("emergencias", e.target.value)} />

            <div className="seccion-sep"></div>

            {/* POLITICAS */}
            <div style={{ background: "#34c759", padding: "12px 16px", borderRadius: "8px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                className={`check-box ${formData.aceptaPoliticas ? "checked" : ""}`}
                onClick={() => toggleCheck("aceptaPoliticas")}
                style={{
                  background: formData.aceptaPoliticas ? "#1a3a6b" : "white",
                  borderColor: formData.aceptaPoliticas ? "#1a3a6b" : "white",
                  flexShrink: 0
                }}
              ></div>
              <span style={{ color: "white", fontWeight: 700, fontSize: "13px" }}>
                He leido y acepto los codigos de medidas preventivas a aplicar *
              </span>
            </div>

            <div className="seccion-sep"></div>

            {/* FIRMA */}
            <div className="seccion-titulo">Firma y conformidad</div>
            <div style={{ display: "flex", gap: "20px", marginTop: "10px", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "180px" }}>
                <div className="sub-label">Tecnico / Encargado responsable *</div>
                <input className="campo-input" type="text" style={{ width: "100%" }} value={formData.tecnicoFinal} onChange={(e) => handleChange("tecnicoFinal", e.target.value)} required />
              </div>
              <div style={{ flex: 1, minWidth: "180px" }}>
                <div className="sub-label">Firma *</div>
                <div className="firma-canvas-wrap">
                  <canvas
                    ref={canvasRef}
                    style={{ display: "block", width: "100%", height: "100%", touchAction: "none" }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  <button type="button" className="firma-clear-btn" onClick={clearSignature}>Borrar</button>
                </div>
              </div>
            </div>

            <button type="submit" className="btn-pdf" disabled={submitting}>
              {submitting ? "Enviando..." : "Enviar Formulario PRL"}
            </button>

          </form>
        </div>
      </div>
    </>
  );
}
